import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import { green } from "@material-ui/core/colors";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import IconButton from "@material-ui/core/IconButton";
import MoreVert from "@material-ui/icons/MoreVert";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import MicIcon from "@material-ui/icons/Mic";
import SpellcheckIcon from "@material-ui/icons/Spellcheck";
import TranslateIcon from "@material-ui/icons/Translate";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import {
  Divider,
  FormControlLabel,
  Hidden,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
} from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Typography from "@material-ui/core/Typography";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { transformTextWithAi } from "../../services/ai";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";

let Mp3Recorder = null;

const initRecorder = async () => {
  if (!Mp3Recorder) {
    try {
      const MicRecorder = (await import("mic-recorder-to-mp3")).default;
      Mp3Recorder = new MicRecorder({ bitRate: 128 });
    } catch (error) {
      console.error("Failed to initialize recorder:", error);
      return null;
    }
  }
  return Mp3Recorder;
};

const getAudioExtensionByMimeType = (mimeType = "") => {
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("webm")) return "webm";
  if (mimeType.includes("mp4")) return "m4a";
  if (mimeType.includes("mpeg")) return "mp3";
  return "mp3";
};

const getRecordingErrorMessage = error => {
  const errorName = error?.name || "";

  if (errorName === "NotAllowedError" || errorName === "SecurityError") {
    return "Permita o uso do microfone no navegador para gravar áudio.";
  }

  if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
    return "Nenhum microfone foi encontrado no dispositivo.";
  }

  if (errorName === "NotReadableError") {
    return "O microfone está em uso por outro aplicativo.";
  }

  if (errorName === "NotSupportedError") {
    return "Este navegador não suporta gravação de áudio.";
  }

  return "Não foi possível iniciar a gravação de áudio.";
};

const buildMentionKey = participant =>
  `${participant.id}:${participant.number || participant.lid || ""}`;

const getMentionMatch = value => {
  const match = value.match(/(?:^|\s)@([^\s@]*)$/);

  if (!match) {
    return null;
  }

  return {
    query: match[1] || "",
    index: match.index + match[0].lastIndexOf("@")
  };
};

const profileLabels = {
  admin: "Administrador",
  financeiro: "Financeiro",
  atendimento: "Atendimento",
};

const aiTranslateOptions = [
  {
    value: "pt-BR",
    labelKey: "messagesInput.ai.languages.ptBR",
    targetLanguage: "Brazilian Portuguese"
  },
  {
    value: "en",
    labelKey: "messagesInput.ai.languages.en",
    targetLanguage: "English"
  },
  {
    value: "es",
    labelKey: "messagesInput.ai.languages.es",
    targetLanguage: "Spanish"
  },
  {
    value: "fr",
    labelKey: "messagesInput.ai.languages.fr",
    targetLanguage: "French"
  }
];

const buildSignedMessageBody = (message, user) => {
  const customSignature = user?.signature?.trim();
  const fullNameFromParts = [user?.firstName, user?.lastName]
    .map(part => (part || "").trim())
    .filter(Boolean)
    .join(" ");
  const senderName =
    user?.name?.trim() ||
    fullNameFromParts ||
    user?.email?.trim() ||
    "Usuario";
  const profileLabel = profileLabels[user?.profile] || user?.profile || "";
  const fallbackSignature = profileLabel
    ? `${senderName} | ${profileLabel}`
    : senderName;
  const signature = customSignature || fallbackSignature;

  return `_*${signature}*_\n${message.trim()}`;
};

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const buildOutgoingMessageBody = (message, mentions) => {
  let normalizedMessage = message.trim();

  mentions
    .filter(mention => mention.label && mention.number)
    .sort((left, right) => right.label.length - left.label.length)
    .forEach(mention => {
      const pattern = new RegExp(`@${escapeRegExp(mention.label)}`, "g");
      normalizedMessage = normalizedMessage.replace(
        pattern,
        `@${mention.number}`
      );
    });

  return normalizedMessage;
};

const buildUserMessageShortcuts = (user, query = "") => {
  const normalizedQuery = query.trim().toLowerCase();
  const personalShortcuts = [
    {
      key: "user-greeting",
      shortcut: "inicio",
      message: user?.greetingMessage?.trim() || ""
    },
    {
      key: "user-farewell",
      shortcut: "despedida",
      message: user?.farewellMessage?.trim() || ""
    }
  ].filter(item => item.message);

  return personalShortcuts.filter(item => {
    if (!normalizedQuery) {
      return true;
    }

    return (
      item.shortcut.toLowerCase().includes(normalizedQuery) ||
      item.message.toLowerCase().includes(normalizedQuery)
    );
  });
};

const protectMessageMentionsForAi = (message, mentions) => {
  let protectedMessage = message;
  const tokenMap = [];

  mentions
    .filter(mention => mention.label && protectedMessage.includes(`@${mention.label}`))
    .sort((left, right) => right.label.length - left.label.length)
    .forEach((mention, index) => {
      const token = `@@AI_TOKEN_${index}@@`;
      const pattern = new RegExp(`@${escapeRegExp(mention.label)}`, "g");

      protectedMessage = protectedMessage.replace(pattern, token);
      tokenMap.push({
        token,
        value: `@${mention.label}`
      });
    });

  return {
    text: protectedMessage,
    tokenMap
  };
};

const restoreMessageMentionsFromAi = (message, tokenMap) => {
  return tokenMap.reduce((result, tokenEntry) => {
    const pattern = new RegExp(escapeRegExp(tokenEntry.token), "g");
    return result.replace(pattern, tokenEntry.value);
  }, message);
};

const useStyles = makeStyles(theme => ({
  mainWrapper: {
    background: theme.custom.panel,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },

  newMessageBox: {
    background: theme.custom.panel,
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      padding: "4px",
      gap: 4,
    },
  },

  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background: theme.palette.background.paper,
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative",
    border: `1px solid ${theme.custom.border}`,
    [theme.breakpoints.down("sm")]: {
      padding: 4,
      marginRight: 4,
      borderRadius: 16,
    },
  },

  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
    [theme.breakpoints.down("sm")]: {
      paddingLeft: 8,
      fontSize: 14,
      lineHeight: 1.25,
    },
  },

  sendMessageIcons: {
    color: theme.custom.actionIcon,
  },

  signMessageLabel: {
    marginRight: 7,
    color: theme.palette.text.secondary,
  },

  aiToolGroup: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  },

  aiActionButton: {
    borderRadius: 16,
    minWidth: 86,
    textTransform: "none",
  },

  aiLoadingIndicator: {
    color: theme.palette.primary.main,
    marginRight: 6,
  },

  aiStatusText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginLeft: 6,
    whiteSpace: "nowrap",
  },

  uploadInput: {
    display: "none",
  },

  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.custom.panel,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },

  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: `1px solid ${theme.custom.border}`,
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: theme.palette.error.main,
  },

  sendAudioIcon: {
    color: theme.palette.success ? theme.palette.success.main : green[500],
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor: theme.custom.inboundQuoted,
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.secondary.main,
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.primary.light,
  },

  messageContactName: {
    display: "flex",
    color: theme.palette.primary.light,
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.background.paper,
    padding: "2px",
    border: `1px solid ${theme.custom.border}`,
    left: 0,
    width: "100%",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        color: theme.palette.text.primary,
        "&:hover": {
          background: theme.custom.panelSoft,
          cursor: "pointer",
        },
      },
    },
  },
  mentionSecondaryText: {
    color: theme.palette.text.secondary,
    marginLeft: 8,
    fontSize: "0.85em",
  },

  menuItemIcon: {
    minWidth: 36,
  },
}));

const MessageInput = ({ ticketStatus, isGroup, composerAction }) => {
  const classes = useStyles();
  const { ticketId } = useParams();
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTicketClosed = ticketStatus === "closed";

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const [groupParticipants, setGroupParticipants] = useState([]);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const inputRef = useRef();
  const moreActionsButtonRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const [translateMenuAnchorEl, setTranslateMenuAnchorEl] = useState(null);
  const [proofreadStatus, setProofreadStatus] = useState(null);
  const nativeMediaRecorderRef = useRef(null);
  const nativeMediaStreamRef = useRef(null);
  const nativeMediaChunksRef = useRef([]);
  const recorderEngineRef = useRef("mp3");
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);
  const signStorageKey = useMemo(
    () => `signOption:${user?.id || "anonymous"}`,
    [user?.id]
  );
  const [signMessage, setSignMessage] = useState(true);
  const [preferredAiLanguage, setPreferredAiLanguage] = useLocalStorage(
    "preferredAiLanguage",
    "en"
  );

  const isBusy = loading || aiLoading;

  useEffect(() => {
    try {
      const storedValue = localStorage.getItem(signStorageKey);

      if (storedValue === null) {
        setSignMessage(true);
        return;
      }

      const parsedValue = JSON.parse(storedValue);
      setSignMessage(parsedValue !== false);
    } catch (error) {
      setSignMessage(true);
    }
  }, [signStorageKey]);

  const handleToggleSignMessage = checked => {
    setSignMessage(checked);
    try {
      localStorage.setItem(signStorageKey, JSON.stringify(checked));
    } catch (error) {
      console.warn("Unable to persist signature preference", error);
    }
  };

  const releaseNativeMediaResources = () => {
    if (nativeMediaStreamRef.current) {
      nativeMediaStreamRef.current.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (error) {
          console.warn("Failed to stop audio track", error);
        }
      });
    }

    nativeMediaRecorderRef.current = null;
    nativeMediaStreamRef.current = null;
    nativeMediaChunksRef.current = [];
  };

  const startNativeRecorder = async () => {
    if (!navigator?.mediaDevices?.getUserMedia || !window?.MediaRecorder) {
      const notSupportedError = new Error("MediaRecorder is not supported");
      notSupportedError.name = "NotSupportedError";
      throw notSupportedError;
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true
      }
    });

    const preferredMimeTypes = [
      "audio/ogg;codecs=opus",
      "audio/mp4",
      "audio/webm;codecs=opus",
      "audio/webm"
    ];

    const selectedMimeType = preferredMimeTypes.find(mimeType =>
      window.MediaRecorder.isTypeSupported?.(mimeType)
    );

    const mediaRecorder = selectedMimeType
      ? new window.MediaRecorder(stream, { mimeType: selectedMimeType })
      : new window.MediaRecorder(stream);

    nativeMediaChunksRef.current = [];
    mediaRecorder.ondataavailable = event => {
      if (event.data && event.data.size > 0) {
        nativeMediaChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.start(200);
    nativeMediaRecorderRef.current = mediaRecorder;
    nativeMediaStreamRef.current = stream;
  };

  const stopNativeRecorder = cancelRecording =>
    new Promise(resolve => {
      const mediaRecorder = nativeMediaRecorderRef.current;

      if (!mediaRecorder) {
        releaseNativeMediaResources();
        resolve(null);
        return;
      }

      const finalize = () => {
        const chunks = nativeMediaChunksRef.current;
        const mimeType = mediaRecorder.mimeType || "audio/webm";
        releaseNativeMediaResources();

        if (cancelRecording) {
          resolve(null);
          return;
        }

        resolve(new Blob(chunks, { type: mimeType }));
      };

      mediaRecorder.onstop = finalize;
      mediaRecorder.onerror = finalize;

      if (mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
      } else {
        finalize();
      }
    });

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    if (!composerAction?.id) {
      return;
    }

    const nextText = composerAction.text || "";

    setInputMessage(nextText);
    handleLoadQuickAnswer(nextText);

    if (composerAction.replyingMessage) {
      setReplyingMessage(composerAction.replyingMessage);
    }

    if (composerAction.type === "suggest_reply") {
      setProofreadStatus(null);
    }

    inputRef.current?.focus();
  }, [composerAction, setReplyingMessage]);

  useEffect(() => {
    let isMounted = true;

    const loadParticipants = async () => {
      if (!isGroup) {
        setGroupParticipants([]);
        setSelectedMentions([]);
        return;
      }

      try {
        const { data } = await api.get(`/tickets/${ticketId}/participants`);

        if (isMounted) {
          setGroupParticipants(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (isMounted) {
          setGroupParticipants([]);
        }
      }
    };

    loadParticipants();

    return () => {
      isMounted = false;
    };
  }, [isGroup, ticketId]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      releaseNativeMediaResources();
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setSelectedMentions([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  const handleChangeInput = e => {
    const nextValue = e.target.value;
    setInputMessage(nextValue);
    setProofreadStatus(null);
    handleLoadQuickAnswer(nextValue);
  };

  const handleQuickAnswersClick = value => {
    setInputMessage(value);
    setProofreadStatus(null);
    setTypeBar(false);
  };

  const mentionMatch = useMemo(
    () => (isGroup ? getMentionMatch(inputMessage) : null),
    [inputMessage, isGroup]
  );

  const filteredParticipants = useMemo(() => {
    if (!mentionMatch) {
      return [];
    }

    const normalizedQuery = mentionMatch.query.trim().toLowerCase();

    return groupParticipants
      .filter(participant => {
        const name = (participant.name || "").toLowerCase();
        const number = (participant.number || "").toLowerCase();

        if (!normalizedQuery) {
          return true;
        }

        return name.includes(normalizedQuery) || number.includes(normalizedQuery);
      })
      .slice(0, 8);
  }, [groupParticipants, mentionMatch]);

  const selectedAiLanguage = useMemo(() => {
    return (
      aiTranslateOptions.find(option => option.value === preferredAiLanguage) ||
      aiTranslateOptions[1]
    );
  }, [preferredAiLanguage]);

  const selectedAiLanguageLabel = i18n.t(selectedAiLanguage.labelKey);

  const handleMentionClick = participant => {
    if (!mentionMatch) {
      return;
    }

    const mentionLabel = participant.name || participant.number;
    const mentionText = `@${mentionLabel}`;
    const updatedMessage =
      inputMessage.slice(0, mentionMatch.index) +
      mentionText +
      " " +
      inputMessage.slice(mentionMatch.index + mentionMatch.query.length + 1);

    const mentionEntry = {
      key: buildMentionKey(participant),
      label: mentionLabel,
      jid: participant.lid || `${participant.number}@s.whatsapp.net`,
      number: participant.number
    };

    setInputMessage(updatedMessage);
    setProofreadStatus(null);
    setSelectedMentions(prevState => {
      const nextState = prevState.filter(item => item.key !== mentionEntry.key);
      nextState.push(mentionEntry);
      return nextState;
    });
    setTypeBar(false);
    inputRef.current?.focus();
  };

  const handleAddEmoji = e => {
    let emoji = e.native;
    setProofreadStatus(null);
    setInputMessage(prevState => prevState + emoji);
  };

  const handleChangeMedias = e => {
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  const handleInputPaste = e => {
    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async e => {
    setLoading(true);
    e.preventDefault();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach(media => {
      formData.append("medias", media);
      formData.append("body", media.name);
    });

    try {
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
    setMedias([]);
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    setLoading(true);

    const activeMentions = selectedMentions
      .filter(mention => inputMessage.includes(`@${mention.label}`))
      .filter(mention => mention.jid && mention.number);
    const mentions = activeMentions.map(mention => mention.jid);
    const outgoingBody = buildOutgoingMessageBody(inputMessage, activeMentions);

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: signMessage
        ? buildSignedMessageBody(outgoingBody, user)
        : outgoingBody,
      quotedMsg: replyingMessage,
      mentions,
    };
    try {
      await api.post(`/messages/${ticketId}`, message);
    } catch (err) {
      toastError(err);
    }

    setInputMessage("");
    setShowEmoji(false);
    setLoading(false);
    setReplyingMessage(null);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      const recorder = await initRecorder();

      if (recorder) {
        try {
          await recorder.start();
          recorderEngineRef.current = "mp3";
          setRecording(true);
          return;
        } catch (mp3RecorderError) {
          console.warn("Falling back to native recorder", mp3RecorderError);
        }
      }

      await startNativeRecorder();
      recorderEngineRef.current = "native";
      setRecording(true);
    } catch (err) {
      toast.error(getRecordingErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async value => {
    if (isGroup && getMentionMatch(value)) {
      setQuickAnswer([]);
      setTypeBar(false);
      return;
    }

    if (value && value.indexOf("/") === 0) {
      const searchParam = value.substring(1).trim();
      const personalQuickAnswers = buildUserMessageShortcuts(user, searchParam);

      try {
      const { data } = await api.get("/quickAnswers/", {
          params: {
            searchParam
          },
        });

        const mergedQuickAnswers = [
          ...personalQuickAnswers,
          ...(data.quickAnswers || [])
        ];

        setQuickAnswer(mergedQuickAnswers);
        if (mergedQuickAnswers.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setQuickAnswer(personalQuickAnswers);
        setTypeBar(personalQuickAnswers.length > 0);
      }
    } else {
      setQuickAnswer([]);
      setTypeBar(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      let blob = null;

      if (recorderEngineRef.current === "native") {
        blob = await stopNativeRecorder(false);
      } else {
        const recorder = await initRecorder();
        if (!recorder) {
          throw new Error("Recorder not available");
        }
        const [, recordedBlob] = await recorder.stop().getMp3();
        blob = recordedBlob;
      }

      if (!blob || blob.size < 300) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const mimeType =
        blob.type ||
        (recorderEngineRef.current === "native" ? "audio/webm" : "audio/mpeg");
      const extension = getAudioExtensionByMimeType(mimeType);
      const filename = `${new Date().getTime()}.${extension}`;
      const audioBlob = new Blob([blob], { type: mimeType });
      formData.append("medias", audioBlob, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    } finally {
      recorderEngineRef.current = "mp3";
      setRecording(false);
      setLoading(false);
    }
  };

  const handleCancelAudio = async () => {
    try {
      if (recorderEngineRef.current === "native") {
        await stopNativeRecorder(true);
      } else {
        const recorder = await initRecorder();
        if (recorder) {
          await recorder.stop().getMp3();
        }
      }
      setRecording(false);
    } catch (err) {
      toast.error("Não foi possível cancelar a gravação.");
    } finally {
      recorderEngineRef.current = "mp3";
      releaseNativeMediaResources();
    }
  };

  const handleOpenMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = () => {
    setAnchorEl(null);
  };

  const handleOpenAiMenu = event => {
    setTranslateMenuAnchorEl(event.currentTarget);
  };

  const handleCloseAiMenu = () => {
    setTranslateMenuAnchorEl(null);
  };

  const handleOpenAiMenuFromMobile = () => {
    setAnchorEl(null);
    setTranslateMenuAnchorEl(moreActionsButtonRef.current);
  };

  const handleTransformText = async ({ action, targetLanguage, languageValue }) => {
    if (isBusy || isTicketClosed || !inputMessage.trim()) {
      return;
    }

    const activeMentions = selectedMentions.filter(mention =>
      inputMessage.includes(`@${mention.label}`)
    );
    const protectedMessage = protectMessageMentionsForAi(
      inputMessage,
      activeMentions
    );

    if (languageValue) {
      setPreferredAiLanguage(languageValue);
    }

    setAiLoading(true);
    setAnchorEl(null);
    setTranslateMenuAnchorEl(null);

    try {
      const data = await transformTextWithAi({
        action,
        text: protectedMessage.text,
        targetLanguage
      });

      const nextMessage = restoreMessageMentionsFromAi(
        data.text || "",
        protectedMessage.tokenMap
      );

      const normalizedCurrentMessage = inputMessage.trim();
      const normalizedNextMessage = nextMessage.trim();

      setInputMessage(nextMessage);
      handleLoadQuickAnswer(nextMessage);

      if (action === "proofread") {
        if (normalizedCurrentMessage === normalizedNextMessage) {
          setProofreadStatus("clean");
          toast.info(i18n.t("messagesInput.ai.proofreadClean"));
        } else {
          setProofreadStatus("updated");
          toast.success(i18n.t("messagesInput.ai.proofreadUpdated"));
        }
      } else {
        setProofreadStatus(null);
      }

      inputRef.current?.focus();
    } catch (err) {
      toastError(err);
    }

    setAiLoading(false);
  };

  const handleProofreadText = () => {
    handleTransformText({ action: "proofread" });
  };

  const handleTranslateText = option => {
    handleTransformText({
      action: "translate",
      targetLanguage: option.targetLanguage,
      languageValue: option.value
    });
  };

  const renderReplyingMessage = message => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {message.body}
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || isTicketClosed}
          onClick={() => setReplyingMessage(null)}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  if (medias.length > 0)
    return (
      <Paper elevation={0} square className={classes.viewMediaInputWrapper}>
        <IconButton
          aria-label="cancel-upload"
          component="span"
          onClick={e => setMedias([])}
        >
          <CancelIcon className={classes.sendMessageIcons} />
        </IconButton>

        {loading ? (
          <div>
            <CircularProgress className={classes.circleLoading} />
          </div>
        ) : (
          <span>
            {medias[0]?.name}
            {/* <img src={media.preview} alt=""></img> */}
          </span>
        )}
        <IconButton
          aria-label="send-upload"
          component="span"
          onClick={handleUploadMedia}
          disabled={loading}
        >
          <SendIcon className={classes.sendMessageIcons} />
        </IconButton>
      </Paper>
    );
  else {
    return (
      <Paper square elevation={0} className={classes.mainWrapper}>
        {replyingMessage && renderReplyingMessage(replyingMessage)}
        <div className={classes.newMessageBox}>
          <Hidden only={["sm", "xs"]}>
            <IconButton
              aria-label="emojiPicker"
              component="span"
              disabled={isBusy || recording || isTicketClosed}
              onClick={e => setShowEmoji(prevState => !prevState)}
            >
              <MoodIcon className={classes.sendMessageIcons} />
            </IconButton>
            {showEmoji ? (
              <div className={classes.emojiBox}>
                <ClickAwayListener onClickAway={e => setShowEmoji(false)}>
                  <Picker
                    perLine={16}
                    showPreview={false}
                    showSkinTones={false}
                    onSelect={handleAddEmoji}
                  />
                </ClickAwayListener>
              </div>
            ) : null}

            <input
              multiple
              type="file"
              id="upload-button"
              disabled={isBusy || recording || isTicketClosed}
              className={classes.uploadInput}
              onChange={handleChangeMedias}
            />
            <label htmlFor="upload-button">
              <IconButton
                aria-label="upload"
                component="span"
                disabled={isBusy || recording || isTicketClosed}
              >
                <AttachFileIcon className={classes.sendMessageIcons} />
              </IconButton>
            </label>
            <div className={classes.aiToolGroup}>
              {aiLoading && (
                <CircularProgress size={18} className={classes.aiLoadingIndicator} />
              )}
              <Button
                variant="outlined"
                size="small"
                className={classes.aiActionButton}
                startIcon={<SpellcheckIcon className={classes.sendMessageIcons} />}
                disabled={isBusy || recording || isTicketClosed || !inputMessage.trim()}
                onClick={handleOpenAiMenu}
              >
                {i18n.t("messagesInput.ai.label")}
              </Button>
              {proofreadStatus && (
                <Typography className={classes.aiStatusText}>
                  {proofreadStatus === "clean"
                    ? i18n.t("messagesInput.ai.statusClean")
                    : i18n.t("messagesInput.ai.statusUpdated")}
                </Typography>
              )}
            </div>
            <FormControlLabel
              className={classes.signMessageLabel}
              label={i18n.t("messagesInput.signMessage")}
              labelPlacement="start"
              control={
                <Switch
                  size="small"
                  checked={signMessage}
                  onChange={e => {
                    handleToggleSignMessage(e.target.checked);
                  }}
                  name="showAllTickets"
                  color="primary"
                />
              }
            />
          </Hidden>
          <Hidden only={["md", "lg", "xl"]}>
            <IconButton
              aria-controls="simple-menu"
              aria-haspopup="true"
              ref={moreActionsButtonRef}
              onClick={handleOpenMenuClick}
            >
              <MoreVert></MoreVert>
            </IconButton>
            <Menu
              id="simple-menu"
              keepMounted
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuItemClick}
            >
              <MenuItem onClick={handleMenuItemClick}>
                <IconButton
                  aria-label="emojiPicker"
                  component="span"
                  disabled={isBusy || recording || isTicketClosed}
                  onClick={e => setShowEmoji(prevState => !prevState)}
                >
                  <MoodIcon className={classes.sendMessageIcons} />
                </IconButton>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <input
                  multiple
                  type="file"
                  id="upload-button"
                  disabled={isBusy || recording || isTicketClosed}
                  className={classes.uploadInput}
                  onChange={handleChangeMedias}
                />
                <label htmlFor="upload-button">
                  <IconButton
                    aria-label="upload"
                    component="span"
                    disabled={isBusy || recording || isTicketClosed}
                  >
                    <AttachFileIcon className={classes.sendMessageIcons} />
                  </IconButton>
                </label>
              </MenuItem>
              <MenuItem
                onClick={handleOpenAiMenuFromMobile}
                disabled={
                  isBusy ||
                  recording ||
                  isTicketClosed ||
                  !inputMessage.trim()
                }
              >
                <ListItemIcon className={classes.menuItemIcon}>
                  <SpellcheckIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={i18n.t("messagesInput.ai.label")}
                />
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleMenuItemClick}>
                <FormControlLabel
                  className={classes.signMessageLabel}
                  label={i18n.t("messagesInput.signMessage")}
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={signMessage}
                      onChange={e => {
                        handleToggleSignMessage(e.target.checked);
                      }}
                      name="showAllTickets"
                      color="primary"
                    />
                  }
                />
              </MenuItem>
            </Menu>
          </Hidden>
          <div className={classes.messageInputWrapper}>
            <InputBase
              inputRef={input => {
                input && input.focus();
                input && (inputRef.current = input);
              }}
              className={classes.messageInput}
              placeholder={
                isTicketClosed
                  ? i18n.t("messagesInput.placeholderClosed")
                  : i18n.t("messagesInput.placeholderOpen")
              }
              multiline
              maxRows={isMobile ? 3 : 5}
              value={inputMessage}
              onChange={handleChangeInput}
              disabled={recording || isBusy || isTicketClosed}
              onPaste={e => {
                !isTicketClosed && handleInputPaste(e);
              }}
              onKeyPress={e => {
                if (isBusy || e.shiftKey) return;
                else if (e.key === "Enter") {
                  handleSendMessage();
                }
              }}
            />
            {mentionMatch && filteredParticipants.length > 0 ? (
              <ul className={classes.messageQuickAnswersWrapper}>
                {filteredParticipants.map(participant => (
                  <li
                    className={classes.messageQuickAnswersWrapperItem}
                    key={buildMentionKey(participant)}
                  >
                    <a onClick={() => handleMentionClick(participant)}>
                      @{participant.name || participant.number}
                      <span className={classes.mentionSecondaryText}>
                        {participant.number}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            ) : typeBar ? (
              <ul className={classes.messageQuickAnswersWrapper}>
                {quickAnswers.map((value, index) => {
                  return (
                    <li
                      className={classes.messageQuickAnswersWrapperItem}
                      key={index}
                    >
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a onClick={() => handleQuickAnswersClick(value.message)}>
                        {`${value.shortcut} - ${value.message}`}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div></div>
            )}
          </div>
          {inputMessage ? (
            <IconButton
              aria-label="sendMessage"
              component="span"
              onClick={handleSendMessage}
              disabled={isBusy}
            >
              <SendIcon className={classes.sendMessageIcons} />
            </IconButton>
          ) : recording ? (
            <div className={classes.recorderWrapper}>
              <IconButton
                aria-label="cancelRecording"
                component="span"
                fontSize="large"
                disabled={isBusy}
                onClick={handleCancelAudio}
              >
                <HighlightOffIcon className={classes.cancelAudioIcon} />
              </IconButton>
              {isBusy ? (
                <div>
                  <CircularProgress className={classes.audioLoading} />
                </div>
              ) : (
                <RecordingTimer />
              )}

              <IconButton
                aria-label="sendRecordedAudio"
                component="span"
                onClick={handleUploadAudio}
                disabled={isBusy}
              >
                <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
              </IconButton>
            </div>
          ) : (
            <IconButton
              aria-label="showRecorder"
              component="span"
              disabled={isBusy || isTicketClosed}
              onClick={handleStartRecording}
            >
              <MicIcon className={classes.sendMessageIcons} />
            </IconButton>
          )}
        </div>
        <Menu
          id="translate-menu"
          keepMounted
          anchorEl={translateMenuAnchorEl}
          open={Boolean(translateMenuAnchorEl)}
          onClose={handleCloseAiMenu}
        >
          <MenuItem
            onClick={handleProofreadText}
            disabled={isBusy || recording || isTicketClosed || !inputMessage.trim()}
          >
            <ListItemIcon className={classes.menuItemIcon}>
              <SpellcheckIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={i18n.t("messagesInput.ai.proofread")} />
          </MenuItem>
          <Divider />
          {aiTranslateOptions.map(option => (
            <MenuItem
              key={option.value}
              selected={selectedAiLanguage.value === option.value}
              onClick={() => handleTranslateText(option)}
            >
              <ListItemIcon className={classes.menuItemIcon}>
                <TranslateIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={i18n.t("messagesInput.ai.translateTo", {
                  language: i18n.t(option.labelKey)
                })}
              />
            </MenuItem>
          ))}
        </Menu>
      </Paper>
    );
  }
};

export default MessageInput;
